import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, IconButton,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin, isAuthenticated } from '../../services/authService';
import { format } from 'date-fns';

const PRIMARY = '#1A2E22';
const PRIMARY_DARK = '#0F1A14';
const MUTED = '#666666';
const SURFACE = '#F8F9FA';

const PILLAR_OPTIONS = [
  '', 'Core Platform & Runtime Health', 'Third-Party Integration Reliability',
  'Safe Delivery'
];
const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed', 'Cancelled'];

const STATUS_COLOR = {
  Completed:    { bg: '#e8f5e9', color: '#2e7d32', accent: '#2e7d32' },
  'In Progress':{ bg: '#e3f2fd', color: '#1565c0', accent: '#1565c0' },
  Cancelled:    { bg: '#f5f5f5', color: '#757575', accent: '#9e9e9e' },
  Pending:      { bg: '#fff8e1', color: '#f57f17', accent: '#f57f17' }
};

const EMPTY_FORM = {
  title: '', description: '', status: 'Pending',
  ownedBy: '', dueDate: '', decisionDate: '', progressComments: '', pillar: ''
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }
};

const ReliabilityDecisions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPillar, setFilterPillar] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await get('/api/reliability/decisions');
      if (res.success) setDecisions(res.data || []);
      else setError('Failed to load decisions');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      title: d.title || '', description: d.description || '',
      status: d.status || 'Pending', ownedBy: d.ownedBy || '',
      dueDate: d.dueDate ? d.dueDate.substring(0, 10) : '',
      decisionDate: d.decisionDate ? d.decisionDate.substring(0, 10) : '',
      progressComments: d.progressComments || '', pillar: d.pillar || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    try {
      setSaving(true); setError(null);
      const res = editing
        ? await authPut(`/api/reliability/decisions/${editing._id}`, form)
        : await authPost('/api/reliability/decisions', form);
      if (res.success) {
        setSuccess(editing ? 'Decision updated.' : 'Decision created.');
        setTimeout(() => setSuccess(null), 4000);
        setDialogOpen(false);
        loadData();
      } else {
        setError(res.message || 'Failed to save');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await authDelete(`/api/reliability/decisions/${deleteTarget._id}`);
      setDeleteTarget(null);
      loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = decisions.filter(d => {
    if (filterStatus && d.status !== filterStatus) return false;
    if (filterPillar && d.pillar !== filterPillar) return false;
    return true;
  });

  const isOverdue = (d) =>
    !!d.dueDate && !['Completed', 'Cancelled'].includes(d.status) && new Date(d.dueDate) < new Date();

  return (
    <Box>
      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2.5, borderRadius: '16px', border: '1.5px solid rgba(0,0,0,0.08)' }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body1" fontWeight={600} color={PRIMARY}>Decisions</Typography>

          <FormControl size="small" sx={{ minWidth: 150, ...inputSx }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 240, ...inputSx }}>
            <InputLabel>Pillar</InputLabel>
            <Select
              value={filterPillar}
              onChange={e => setFilterPillar(e.target.value)}
              label="Pillar"
            >
              {PILLAR_OPTIONS.map(p => <MenuItem key={p} value={p}>{p || 'All Pillars'}</MenuItem>)}
            </Select>
          </FormControl>

          <Typography variant="body2" color={MUTED} sx={{ mr: 'auto' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </Typography>

          {isAuthenticated() && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreate}
              aria-label="Add a new decision"
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }
              }}
            >
              Add Decision
            </Button>
          )}
        </Box>
      </Paper>

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : (
        <Box>
          {filtered.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color={MUTED} variant="body2">No decisions found.</Typography>
            </Box>
          )}
          {filtered.map((d) => {
            const overdue = isOverdue(d);
            const sc = STATUS_COLOR[d.status] || STATUS_COLOR.Pending;
            return (
              <Paper
                key={d._id}
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  border: '1.5px solid rgba(0,0,0,0.07)',
                  mb: 1.5,
                  overflow: 'hidden',
                  display: 'flex',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }
                }}
              >
                <Box sx={{ width: 5, bgcolor: sc.accent, flexShrink: 0 }} />
                <Box sx={{ flex: 1, px: 2.5, py: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.75} flexWrap="wrap">
                    <Chip
                      label={d.status}
                      size="small"
                      sx={{
                        bgcolor: sc.bg, color: sc.color,
                        fontWeight: 600, fontSize: '0.75rem', border: 'none',
                        borderRadius: '8px'
                      }}
                    />
                    {d.pillar && (
                      <Chip
                        label={d.pillar}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', borderColor: `${PRIMARY}40`, color: PRIMARY, borderRadius: '8px' }}
                      />
                    )}
                  </Box>

                  <Typography variant="body1" fontWeight={600} color="#1a1a1a" mb={0.4}>
                    {d.title}
                  </Typography>

                  {d.description && (
                    <Typography
                      variant="body2"
                      color={MUTED}
                      mb={0.4}
                      sx={{
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {d.description}
                    </Typography>
                  )}

                  {d.progressComments && (
                    <Typography
                      variant="caption"
                      color={MUTED}
                      display="block"
                      mb={0.75}
                      sx={{
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {d.progressComments}
                    </Typography>
                  )}

                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mt={1}>
                    <Box display="flex" alignItems="center" gap={2} flex={1} flexWrap="wrap">
                      {d.ownedBy && (
                        <Typography variant="caption" color={MUTED}>
                          Owner: <strong>{d.ownedBy}</strong>
                        </Typography>
                      )}
                      {d.dueDate && (
                        <Typography
                          variant="caption"
                          color={overdue ? 'error.main' : MUTED}
                          fontWeight={overdue ? 600 : 400}
                        >
                          Due: {format(new Date(d.dueDate), 'dd MMM yyyy')}
                          {overdue && ' · Overdue'}
                        </Typography>
                      )}
                      {d.decisionDate && (
                        <Typography variant="caption" color={MUTED}>
                          Dec: {format(new Date(d.decisionDate), 'dd MMM yyyy')}
                        </Typography>
                      )}
                    </Box>

                    {isAuthenticated() && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(d)}
                            aria-label={`Edit decision: ${d.title}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isAdmin() && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(d)}
                              aria-label={`Delete decision: ${d.title}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: PRIMARY, pb: 1 }}>
          {editing ? 'Edit Decision' : 'Add Decision'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Title *"
            value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            fullWidth size="small" sx={inputSx}
            autoFocus
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            fullWidth size="small" multiline rows={2} sx={inputSx}
          />
          <Box display="flex" gap={2}>
            <FormControl size="small" fullWidth sx={inputSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                label="Status"
              >
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={inputSx}>
              <InputLabel>Pillar</InputLabel>
              <Select
                value={form.pillar}
                onChange={e => setForm(p => ({ ...p, pillar: e.target.value }))}
                label="Pillar"
              >
                {PILLAR_OPTIONS.map(p => <MenuItem key={p} value={p}>{p || 'None'}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Owned By"
            value={form.ownedBy}
            onChange={e => setForm(p => ({ ...p, ownedBy: e.target.value }))}
            fullWidth size="small" sx={inputSx}
          />
          <Box display="flex" gap={2}>
            <TextField
              label="Due Date"
              type="date"
              value={form.dueDate}
              onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
              fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
            <TextField
              label="Decision Date"
              type="date"
              value={form.decisionDate}
              onChange={e => setForm(p => ({ ...p, decisionDate: e.target.value }))}
              fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Box>
          <TextField
            label="Progress Comments"
            value={form.progressComments}
            onChange={e => setForm(p => ({ ...p, progressComments: e.target.value }))}
            fullWidth size="small" multiline rows={3} sx={inputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '10px', color: MUTED }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '10px',
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }
            }}
          >
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Decision'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Decision</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteTarget?.title}"</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: 'none', borderRadius: '10px', color: MUTED }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReliabilityDecisions;
